import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  PERMISSIONS,
  PERMISSION_GROUPS,
  PERMISSION_GROUP_LABELS,
  PERMISSION_KEYS,
  REPORTS,
  REPORT_GROUP_LABELS,
  REPORT_KEYS,
  formatDate,
  sortReports,
  withRequirements,
  withoutDependents,
  type Permission,
  type PermissionGroup,
  type ReportGroup,
  type ReportKey,
  type RoleAccessRow,
  type RolesResponse,
  type UserRole,
} from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import {
  Alert,
  Badge,
  Button,
  Card,
  Input,
  Label,
  Modal,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@/components/ui';
import { PageHeader } from '@/components/domain';
import { cn } from '@/lib/cn';

/**
 * /admin/roller - hvad hver rolle maa. Kun admin.
 *
 * Én raekke pr. rettighed og én kolonne pr. rolle, og i gruppen Rapporter de rapporter, der
 * vaelges i et vindue. Rettighederne, deres navne og hvad de kraever, staar i @codepass/shared;
 * hvad den enkelte rolle har, staar i databasen - se docs/roller-opsaetning.md.
 *
 * The groups - Overblik, Kontrakter and so on - fold, and start folded: folded, a group shows how
 * many of its rights each role has. The 'Alle' column sets or clears a right for every role that
 * can be edited at once.
 *
 * **Gemmes samlet**, ikke ved hvert kryds: et kryds kan saette eller fjerne andre - 'Lav
 * fakturaudkast' kraever 'Se fakturaudkast' - og et saet, der kun var halvt gemt, ville give en
 * rolle noget, ingen havde valgt. Kravene saettes her, men API'et er det, der afviser.
 *
 * Admin staar med alt og kan ikke rettes - se `LOCKED_ROLES`.
 *
 * With 'Opret og slet roller' an admin can make roles of their own: "Ny rolle" adds a column with
 * no rights, and every role - the four the roles started with too - can be renamed or, when nobody
 * has it, deleted under its name. That is not saved with the rest: it is done the moment it is
 * confirmed, and the drafts on the other roles stay.
 */

const QUERY_KEY = ['admin', 'roles'];

const REPORT_GROUPS: readonly ReportGroup[] = ['finance', 'operations'];

interface Draft {
  permissions: Permission[];
  reports: ReportKey[];
}

type Drafts = Partial<Record<UserRole, Draft>>;

type TogglePermission = (row: RoleAccessRow, permission: Permission, on: boolean) => void;
type TogglePermissionForAll = (permission: Permission, on: boolean) => void;

function sameSet<T>(a: readonly T[], b: readonly T[]): boolean {
  return a.length === b.length && a.every((value) => b.includes(value));
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/** A right with what it requires, or without what requires it - what a single tick does. */
function withPermission(permissions: readonly Permission[], permission: Permission, on: boolean) {
  return on
    ? withRequirements([...permissions, permission])
    : withoutDependents(permissions, permission);
}

export function AdminRolesPage() {
  const queryClient = useQueryClient();
  const { refreshUser, can } = useAuth();
  /* 'Se roller og rettigheder' uden 'Ændr': matricen til at laese. */
  const canEdit = can('roles.manage');
  const canCreate = can('roles.create');
  const [drafts, setDrafts] = useState<Drafts>({});
  const [choosingReports, setChoosingReports] = useState<UserRole | null>(null);
  /* The name dialog: a new role, or renaming one. Null when it is closed. */
  const [naming, setNaming] = useState<{ row: RoleAccessRow | null } | null>(null);
  const [deleting, setDeleting] = useState<RoleAccessRow | null>(null);
  const [result, setResult] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  /* The groups that are unfolded. None at first, so the page opens as an overview. */
  const [open, setOpen] = useState<ReadonlySet<PermissionGroup>>(() => new Set());

  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<RolesResponse>('/roles'),
    /* Rettes ogsaa i SQL-editoren - samme forbehold som /admin/brugere. */
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const roles = data?.roles ?? [];
  /* Admin - whatever it is called now. Gone if it has been deleted. */
  const lockedRole = roles.find((row) => row.locked) ?? null;
  /** Det, der staar paa skaermen: kladden, hvis rollen er rettet, ellers det gemte. */
  const current = (row: RoleAccessRow): Draft =>
    drafts[row.role] ?? { permissions: row.permissions, reports: row.reports };

  const changed = roles.filter((row) => {
    const draft = drafts[row.role];
    return (
      draft &&
      (!sameSet(draft.permissions, row.permissions) || !sameSet(draft.reports, row.reports))
    );
  });

  /* En kladde, der er gemt, eller som er rettet tilbage til det gemte, er ikke laengere en kladde. */
  useEffect(() => {
    if (!data) return;
    setDrafts((previous) => {
      const next: Drafts = {};
      for (const row of data.roles) {
        const draft = previous[row.role];
        if (
          draft &&
          (!sameSet(draft.permissions, row.permissions) || !sameSet(draft.reports, row.reports))
        ) {
          next[row.role] = draft;
        }
      }
      return next;
    });
  }, [data]);

  const update = (row: RoleAccessRow, change: (draft: Draft) => Draft): void => {
    setResult(null);
    setDrafts((previous) => ({ ...previous, [row.role]: change(current(row)) }));
  };

  const togglePermission: TogglePermission = (row, permission, on) =>
    update(row, (draft) => ({
      ...draft,
      permissions: withPermission(draft.permissions, permission, on),
    }));

  /** The 'Alle' column: the same tick on every role that can be edited, in one go. */
  const togglePermissionForAll: TogglePermissionForAll = (permission, on) => {
    setResult(null);
    setDrafts((previous) => {
      const next: Drafts = { ...previous };
      for (const row of roles) {
        if (row.locked) continue;
        const draft = previous[row.role] ?? { permissions: row.permissions, reports: row.reports };
        next[row.role] = {
          ...draft,
          permissions: withPermission(draft.permissions, permission, on),
        };
      }
      return next;
    });
  };

  const toggleGroup = (group: PermissionGroup): void =>
    setOpen((previous) => {
      const next = new Set(previous);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  const allOpen = open.size === PERMISSION_GROUPS.length;

  /**
   * Én rolle ad gangen, i raekkefoelge. Fejler én, stopper det dér: de foregaaende er gemt, og
   * beskeden siger, hvilken der ikke blev det.
   */
  const save = useMutation({
    mutationFn: async () => {
      let latest: RolesResponse | null = null;
      for (const row of changed) {
        const draft = current(row);
        try {
          latest = await api.put<RolesResponse>(`/roles/${row.role}`, {
            permissions: draft.permissions,
            reports: draft.reports,
          });
        } catch (cause) {
          throw new Error(`${row.label}: ${errorText(cause, 'Kunne ikke gemme.')}`, { cause });
        }
      }
      return latest;
    },
    onSuccess: async (latest) => {
      if (latest) queryClient.setQueryData(QUERY_KEY, latest);
      setResult({ tone: 'success', text: 'Rettighederne er gemt.' });
      /* Ens egen rolle kan vaere rettet - saa skal menuen ogsaa. */
      await refreshUser();
    },
    onError: async (cause) => {
      setResult({ tone: 'error', text: errorText(cause, 'Kunne ikke gemme rettighederne.') });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  /**
   * Create, rename and delete. Each is done when it is confirmed - not with "Gem ændringer" - and
   * answers with every role, so the page shows the result right away. A deleted role's draft goes
   * with it: the effect above only keeps drafts for roles that are still there.
   */
  const afterRoleChange = async (latest: RolesResponse, text: string): Promise<void> => {
    queryClient.setQueryData(QUERY_KEY, latest);
    await queryClient.invalidateQueries({ queryKey: ['roles', 'options'] });
    setNaming(null);
    setDeleting(null);
    setResult({ tone: 'success', text });
  };

  const saveName = useMutation({
    mutationFn: (input: { row: RoleAccessRow | null; label: string }) =>
      input.row
        ? api.patch<RolesResponse>(`/roles/${input.row.role}`, { label: input.label })
        : api.post<RolesResponse>('/roles', { label: input.label }),
    onSuccess: (latest, input) =>
      afterRoleChange(
        latest,
        input.row
          ? `${input.row.label} hedder nu ${input.label.trim()}.`
          : `${input.label.trim()} er oprettet uden rettigheder. Kryds af, hvad rollen må, og gem.`,
      ),
  });

  const removeRole = useMutation({
    mutationFn: (row: RoleAccessRow) => api.del<RolesResponse>(`/roles/${row.role}`),
    onSuccess: (latest, row) => afterRoleChange(latest, `${row.label} er slettet.`),
  });

  const choosing = roles.find((row) => row.role === choosingReports) ?? null;

  /* The report selection is the first row of Rapporter - a window per role, not a tick. */
  const reportsRow: GroupExtraRow = {
    has: (row) => current(row).reports.length > 0,
    node: (
      <Tr>
        <Td>
          <span className="block font-medium">Se udvalgte rapporter</span>
          <span className="block text-xs text-ink-muted">
            De rapporter på /rapporter, rollen kan se. Menupunktet vises, når der er mindst én.
          </span>
        </Td>
        <Td className="text-center text-xs text-ink-muted" title="Rapporterne vælges pr. rolle">
          —
        </Td>
        {roles.map((row) => {
          const count = current(row).reports.length;
          return (
            <Td key={row.role} className="text-center">
              <span className="block text-xs tabular">
                {count === REPORT_KEYS.length ? 'Alle' : `${count} af ${REPORT_KEYS.length}`}
              </span>
              {!row.locked ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="mt-0.5"
                  onClick={() => setChoosingReports(row.role)}
                >
                  {canEdit ? 'Vælg' : 'Se'}
                </Button>
              ) : null}
            </Td>
          );
        })}
      </Tr>
    ),
  };

  return (
    <>
      <PageHeader
        title="Roller og rettigheder"
        description={
          canEdit
            ? `Hvad hver rolle må se og gøre.${
                lockedRole ? ` ${lockedRole.label} har altid alle rettigheder.` : ''
              }`
            : 'Hvad hver rolle må se og gøre. Din rolle kan se rettighederne, men ikke ændre dem.'
        }
        actions={
          canEdit || canCreate ? (
            <>
              {canCreate ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!data?.custom_roles_ready}
                  title={
                    data && !data.custom_roles_ready
                      ? 'Kør SQL’en under "Egne roller" i docs/roller-opsaetning.md først'
                      : undefined
                  }
                  onClick={() => {
                    saveName.reset();
                    setNaming({ row: null });
                  }}
                >
                  Ny rolle
                </Button>
              ) : null}
              {changed.length > 0 ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={save.isPending}
                  onClick={() => {
                    setDrafts({});
                    setResult(null);
                  }}
                >
                  Fortryd
                </Button>
              ) : null}
              <Button
                size="sm"
                disabled={changed.length === 0 || !data?.ready}
                loading={save.isPending}
                onClick={() => save.mutate()}
              >
                Gem ændringer
              </Button>
            </>
          ) : undefined
        }
      />

      {result ? (
        <Alert tone={result.tone} className="mb-4" onDismiss={() => setResult(null)}>
          {result.text}
        </Alert>
      ) : null}

      {data && !data.ready ? (
        <Alert tone="warning" className="mb-4" title="Tabellerne til roller er ikke sat op">
          Kør SQL’en i docs/roller-opsaetning.md i Supabase’s SQL-editor. Indtil da gælder
          standardrettighederne nedenfor, og der kan ikke gemmes.
        </Alert>
      ) : null}

      {canCreate && data?.ready && !data.custom_roles_ready ? (
        <Alert tone="info" className="mb-4" title="Egne roller er ikke sat op">
          Kør SQL’en under &quot;Egne roller&quot; i docs/roller-opsaetning.md i Supabase’s
          SQL-editor. Indtil da kan roller hverken oprettes, omdøbes eller slettes — rollerne virker
          som før.
        </Alert>
      ) : null}

      {isLoading ? (
        <Skeleton className="h-96" />
      ) : error || !data ? (
        <Alert tone="error">{errorText(error, 'Kunne ikke hente rollerne.')}</Alert>
      ) : (
        <Card>
          {/* The role names stay at the top while the rights scroll under them - see `stickyHeader`. */}
          <Table stickyHeader>
            <thead>
              <tr>
                <Th>
                  <span className="flex items-center justify-between gap-2">
                    Rettighed
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setOpen(allOpen ? new Set() : new Set(PERMISSION_GROUPS))}
                    >
                      {allOpen ? 'Fold alle sammen' : 'Fold alle ud'}
                    </Button>
                  </span>
                </Th>
                <Th className="w-[120px] text-center">
                  <span className="block text-ink">Alle</span>
                  <span className="block font-normal">alle roller</span>
                </Th>
                {roles.map((row) => (
                  <Th key={row.role} className="w-[120px] text-center">
                    <span className="block text-ink">{row.label}</span>
                    <span className="block font-normal">
                      {row.user_count === 1 ? '1 bruger' : `${row.user_count} brugere`}
                    </span>
                    {changed.includes(row) ? (
                      <Badge tone="warning" className="mt-1">
                        Ikke gemt
                      </Badge>
                    ) : null}
                    {/*
                      Every role - the four the roles started with too. Not before the SQL for
                      created roles has run: the notice above says so.
                    */}
                    {canCreate && data.custom_roles_ready ? (
                      <span className="mt-1 flex justify-center gap-1 font-normal">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-1.5 text-xs"
                          onClick={() => {
                            saveName.reset();
                            setNaming({ row });
                          }}
                        >
                          Omdøb
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-1.5 text-xs"
                          disabled={row.user_count > 0}
                          title={
                            row.user_count > 0
                              ? 'Giv rollens brugere en anden rolle på /admin/brugere først'
                              : undefined
                          }
                          onClick={() => {
                            removeRole.reset();
                            setDeleting(row);
                          }}
                        >
                          Slet
                        </Button>
                      </span>
                    ) : null}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERMISSION_GROUPS.map((group) => (
                <PermissionGroupRows
                  key={group}
                  group={group}
                  open={open.has(group)}
                  onToggleOpen={() => toggleGroup(group)}
                  roles={roles}
                  current={current}
                  onToggle={canEdit ? togglePermission : null}
                  onToggleAll={canEdit ? togglePermissionForAll : null}
                  extra={group === 'reports' ? reportsRow : null}
                />
              ))}
            </tbody>
          </Table>

          <p className="border-t border-line px-4 py-3 text-xs text-ink-muted">
            {roles
              .filter((row) => !row.locked)
              .map((row) =>
                row.configured && row.updated_at
                  ? `${row.label}: rettet ${formatDate(row.updated_at)}${
                      row.updated_by_name ? ` af ${row.updated_by_name}` : ''
                    }`
                  : `${row.label}: standardrettighederne`,
              )
              .join(' · ')}
            . En ændring slår igennem inden for et halvt minut — også for brugere, der er logget
            ind.
          </p>
        </Card>
      )}

      {naming ? (
        <RoleNameModal
          row={naming.row}
          busy={saveName.isPending}
          error={saveName.error ? errorText(saveName.error, 'Kunne ikke gemme rollen.') : null}
          onClose={() => setNaming(null)}
          onSave={(label) => saveName.mutate({ row: naming.row, label })}
        />
      ) : null}

      {deleting ? (
        <Modal
          open
          onClose={() => setDeleting(null)}
          title={`Slet ${deleting.label}?`}
          description="Rollen slettes sammen med dens rettigheder og rapporter. Det kan ikke fortrydes."
          footer={
            <>
              <Button variant="secondary" onClick={() => setDeleting(null)}>
                Annuller
              </Button>
              <Button
                variant="destructive"
                loading={removeRole.isPending}
                onClick={() => removeRole.mutate(deleting)}
              >
                Slet rolle
              </Button>
            </>
          }
        >
          {removeRole.error ? (
            <Alert tone="error">{errorText(removeRole.error, 'Kunne ikke slette rollen.')}</Alert>
          ) : (
            <p className="text-sm text-ink-muted">
              Hvad rollen måtte, står i audit-loggen, hvis den skal laves igen.
            </p>
          )}
        </Modal>
      ) : null}

      {choosing ? (
        <ReportsModal
          label={choosing.label}
          selected={current(choosing).reports}
          onClose={() => setChoosingReports(null)}
          onDone={
            canEdit
              ? (reports) => {
                  update(choosing, (draft) => ({ ...draft, reports }));
                  setChoosingReports(null);
                }
              : null
          }
        />
      ) : null}
    </>
  );
}

/** A row in a group that is not one of its rights - the report selection in Rapporter. */
interface GroupExtraRow {
  /** Whether the role has it, for the count on the folded group. */
  has: (row: RoleAccessRow) => boolean;
  node: ReactNode;
}

function PermissionGroupRows({
  group,
  open,
  onToggleOpen,
  roles,
  current,
  onToggle,
  onToggleAll,
  extra,
}: {
  group: PermissionGroup;
  open: boolean;
  onToggleOpen: () => void;
  roles: readonly RoleAccessRow[];
  current: (row: RoleAccessRow) => Draft;
  /** Null uden 'Ændr roller og rettigheder' - saa er krydserne til at laese. */
  onToggle: TogglePermission | null;
  onToggleAll: TogglePermissionForAll | null;
  extra: GroupExtraRow | null;
}) {
  const permissions = PERMISSION_KEYS.filter((key) => PERMISSIONS[key].group === group);
  const total = permissions.length + (extra ? 1 : 0);
  const countFor = (row: RoleAccessRow): number =>
    permissions.filter((permission) => current(row).permissions.includes(permission)).length +
    (extra?.has(row) ? 1 : 0);
  const editable = roles.filter((row) => !row.locked);
  const headerCell = 'border-b border-line bg-canvas/60 px-3 py-2';

  return (
    <>
      {/*
        The whole row folds the group. The button is there for the keyboard: its click bubbles to
        the row, so it needs no handler of its own - with one, a click would fold twice.
      */}
      <tr className="cursor-pointer hover:bg-canvas" onClick={onToggleOpen}>
        <td className={headerCell}>
          <button
            type="button"
            aria-expanded={open}
            className="flex w-full items-center gap-1.5 text-left text-xs font-semibold uppercase tracking-wide text-ink-muted hover:text-ink"
          >
            {open ? (
              <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
            )}
            {PERMISSION_GROUP_LABELS[group]}
          </button>
        </td>
        <td className={headerCell} />
        {roles.map((row) => (
          <td
            key={row.role}
            className={cn(headerCell, 'text-center text-xs tabular text-ink-muted')}
          >
            {countFor(row)} af {total}
          </td>
        ))}
      </tr>

      {open ? (
        <>
          {extra?.node}
          {permissions.map((permission) => {
            const definition = PERMISSIONS[permission];
            const having = editable.filter((row) =>
              current(row).permissions.includes(permission),
            ).length;
            const all = editable.length > 0 && having === editable.length;
            return (
              <Tr key={permission}>
                <Td>
                  <span className="block font-medium">{definition.label}</span>
                  <span className="block text-xs text-ink-muted">{definition.description}</span>
                  {definition.requires.length > 0 ? (
                    <span className="mt-0.5 block text-xs text-ink-muted">
                      Kræver{' '}
                      {definition.requires
                        .map((required) => PERMISSIONS[required].label)
                        .join(' og ')}
                    </span>
                  ) : null}
                </Td>
                <Td className="text-center">
                  <TriStateCheckbox
                    checked={all}
                    indeterminate={having > 0 && !all}
                    disabled={!onToggleAll || editable.length === 0}
                    label={`${definition.label} for alle roller`}
                    title={
                      onToggleAll
                        ? all
                          ? 'Fjern fra alle roller'
                          : 'Giv til alle roller'
                        : undefined
                    }
                    /* Partly ticked counts as off: a click gives it to everyone. */
                    onChange={() => onToggleAll?.(permission, !all)}
                  />
                </Td>
                {roles.map((row) => {
                  const checked = current(row).permissions.includes(permission);
                  const disabled = row.locked || !onToggle;
                  return (
                    <Td key={row.role} className="text-center">
                      <input
                        type="checkbox"
                        className={cn('h-4 w-4 accent-brand', !disabled && 'cursor-pointer')}
                        aria-label={`${definition.label} for ${row.label}`}
                        checked={checked}
                        disabled={disabled}
                        title={row.locked ? `${row.label} har altid alle rettigheder` : undefined}
                        onChange={(event) => onToggle?.(row, permission, event.target.checked)}
                      />
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </>
      ) : null}
    </>
  );
}

/** A checkbox that can also be partly ticked - `indeterminate` is a property, not an attribute. */
function TriStateCheckbox({
  checked,
  indeterminate,
  disabled,
  label,
  title,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  label: string;
  title: string | undefined;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className={cn('h-4 w-4 accent-brand', !disabled && 'cursor-pointer')}
      aria-label={label}
      checked={checked}
      disabled={disabled}
      title={title}
      onChange={onChange}
    />
  );
}

/**
 * The name of a new role, or a new name for a created one. The server makes the key from it and
 * refuses a name another role has - see `createRole` in the API.
 */
function RoleNameModal({
  row,
  busy,
  error,
  onClose,
  onSave,
}: {
  /** Null for a new role. */
  row: RoleAccessRow | null;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (label: string) => void;
}) {
  const [label, setLabel] = useState(row?.label ?? '');
  const valid = label.trim().length >= 2 && label.trim() !== row?.label;

  return (
    <Modal
      open
      onClose={onClose}
      title={row ? `Omdøb ${row.label}` : 'Ny rolle'}
      description={
        row
          ? 'Brugerne beholder rollen - kun navnet skifter.'
          : 'Rollen oprettes uden rettigheder og rapporter. Kryds af, hvad den må, og gem. Brugere får den på /admin/brugere.'
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annuller
          </Button>
          <Button loading={busy} disabled={!valid} onClick={() => onSave(label)}>
            {row ? 'Omdøb' : 'Opret rolle'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (valid && !busy) onSave(label);
        }}
      >
        <Label htmlFor="role-label">Navn</Label>
        <Input
          id="role-label"
          autoFocus
          maxLength={60}
          placeholder="Fx Projektleder"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />
        {error ? <p className="text-xs text-danger">{error}</p> : null}
      </form>
    </Modal>
  );
}

/** Vinduet bag "Vælg": rapporterne, rollen kan se. Gemmes med resten af siden. */
function ReportsModal({
  label,
  selected,
  onClose,
  onDone,
}: {
  /** The role's name - a created role's own. */
  label: string;
  selected: readonly ReportKey[];
  onClose: () => void;
  /** Null uden 'Ændr roller og rettigheder' - saa viser vinduet blot listen. */
  onDone: ((reports: ReportKey[]) => void) | null;
}) {
  const [chosen, setChosen] = useState<ReportKey[]>([...selected]);
  const all = chosen.length === REPORT_KEYS.length;

  const byGroup = useMemo(
    () =>
      REPORT_GROUPS.map((group) => ({
        group,
        reports: REPORTS.filter((report) => report.group === group),
      })),
    [],
  );

  const toggle = (key: ReportKey, on: boolean): void =>
    setChosen((previous) =>
      sortReports(on ? [...previous, key] : previous.filter((value) => value !== key)),
    );

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`Rapporter for ${label}`}
      description="De rapporter, rollen kan se på /rapporter. At hente dem som PDF eller CSV er “Eksportér rapporter”."
      footer={
        onDone ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              Annuller
            </Button>
            <Button onClick={() => onDone(chosen)}>Færdig</Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onClose}>
            Luk
          </Button>
        )
      }
    >
      {onDone ? (
        <div className="mb-3 flex justify-end">
          <Button size="sm" variant="ghost" onClick={() => setChosen(all ? [] : [...REPORT_KEYS])}>
            {all ? 'Fravælg alle' : 'Vælg alle'}
          </Button>
        </div>
      ) : null}
      <div className="space-y-4">
        {byGroup.map(({ group, reports }) => (
          <section key={group}>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
              {REPORT_GROUP_LABELS[group]}
            </h3>
            <ul className="space-y-2">
              {reports.map((report) => (
                <li key={report.key}>
                  <label className={cn('flex items-start gap-2', onDone && 'cursor-pointer')}>
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-brand"
                      checked={chosen.includes(report.key)}
                      disabled={!onDone}
                      onChange={(event) => toggle(report.key, event.target.checked)}
                    />
                    <span>
                      <span className="block text-sm font-medium">{report.title}</span>
                      <span className="block text-xs text-ink-muted">{report.description}</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Modal>
  );
}
